/**
 * Dashboard Page
 * 
 * Main dashboard view with permission-based UI
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppNavbar from '../components/AppNavbar';
import PermissionGuard from '../components/PermissionGuard';
import authService from '../services/authService';
import competitionService from '../services/competitionService';
import healthService from '../services/healthService';
import './DashboardPage.css';

const DashboardPage = () => {
  const { user, roles, permissions, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [serverHealth, setServerHealth] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [videoSummary, setVideoSummary] = useState({
    loading: true,
    error: '',
    totalCount: 0,
    latestSubmission: null,
    latestCompetition: null
  });
  const userIsAdmin = hasRole('admin');

  useEffect(() => {
    if (!userIsAdmin) {
      return;
    }

    // Check server health on mount
    const checkHealth = async () => {
      try {
        const health = await healthService.checkHealth();
        setServerHealth(health);
      } catch (error) {
        console.error('Failed to fetch server health:', error);
      }
    };

    checkHealth();
  }, [userIsAdmin]);

  useEffect(() => {
    let isMounted = true;

    const loadVideoSummary = async () => {
      setVideoSummary({
        loading: true,
        error: '',
        totalCount: 0,
        latestSubmission: null,
        latestCompetition: null
      });

      try {
        const competitions = await fetchAllCompetitions();
        const submissionPayloads = await Promise.all(
          competitions.map(async (competition) => {
            try {
              const response = await competitionService.getMySubmissions(competition.id);
              const submissions = Array.isArray(response?.submissions) ? response.submissions : [];
              return submissions.map((submission) => ({
                ...submission,
                competition
              }));
            } catch (error) {
              return [];
            }
          })
        );

        const allSubmissions = submissionPayloads.flat();
        const latestSubmission = getLatestSubmission(allSubmissions);

        if (!isMounted) {
          return;
        }

        setVideoSummary({
          loading: false,
          error: '',
          totalCount: allSubmissions.length,
          latestSubmission,
          latestCompetition: latestSubmission?.competition || null
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setVideoSummary({
          loading: false,
          error: 'Unable to load your video submissions right now.',
          totalCount: 0,
          latestSubmission: null,
          latestCompetition: null
        });
      }
    };

    loadVideoSummary();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Navigate anyway since local state is cleared
      navigate('/login');
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordMessage('');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in current password, new password, and confirm password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await authService.changePassword(oldPassword, newPassword);
      setPasswordMessage(response.message || 'Password updated successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowChangePassword(false);
    } catch (error) {
      setPasswordError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Unable to change password right now.'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSubmitVideo = () => {
    navigate('/competitions');
  };

  const handleViewAllSubmissions = () => {
    if (videoSummary.latestCompetition?.id) {
      navigate(`/competitions/${videoSummary.latestCompetition.id}/submissions`);
      return;
    }

    navigate('/competitions');
  };

  const navigationItems = [
    { label: 'Dashboard', to: '/dashboard', isActive: true },
    { label: 'Competitions', to: '/competitions', isActive: false }
  ];

  return (
    <div className="dashboard-page">
      <AppNavbar
        user={user}
        onLogout={handleLogout}
        navigationItems={navigationItems}
        brandTo="/dashboard"
      />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p>Welcome to your Creator Yatra dashboard</p>
        </div>

        <div className="dashboard-grid">
          {/* User Profile Card */}
          <div className="dashboard-card user-card">
            <h3>Your Profile</h3>
            <div className="user-info">
              {user?.profileImage && (
                <img 
                  src={user.profileImage} 
                  alt="Profile" 
                  className="profile-picture"
                />
              )}
              <div className="user-details">
                <div className="info-item">
                  <span className="label">Name:</span>
                  <span className="value">{user?.name || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Email:</span>
                  <span className="value">{user?.email || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Roles:</span>
                  <div className="role-badges">
                    {roles && roles.length > 0 ? (
                      roles.map((role, index) => (
                        <span key={index} className="role-badge">
                          {role.displayName || role.roleName}
                        </span>
                      ))
                    ) : (
                      <span className="value">No roles assigned</span>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <span className="label">Permissions:</span>
                  <span className="value">{permissions?.length || 0} permissions</span>
                </div>
                {user?.isEmailVerified && (
                  <div className="info-item">
                    <span className="verified-badge">✓ Email Verified</span>
                  </div>
                )}

                <div className="profile-actions-row">
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => {
                      setShowChangePassword((currentValue) => !currentValue);
                      setPasswordError('');
                      setPasswordMessage('');
                    }}
                  >
                    {showChangePassword ? 'Cancel Password Change' : 'Change Password'}
                  </button>
                </div>

                {passwordMessage && (
                  <div className="inline-success-message" role="status">
                    {passwordMessage}
                  </div>
                )}

                {passwordError && (
                  <div className="inline-error-message" role="alert">
                    {passwordError}
                  </div>
                )}

                {showChangePassword && (
                  <form className="change-password-form" onSubmit={handleChangePassword}>
                    <div className="form-field-group">
                      <label htmlFor="oldPassword">Current Password</label>
                      <input
                        type="password"
                        id="oldPassword"
                        value={oldPassword}
                        onChange={(event) => setOldPassword(event.target.value)}
                        autoComplete="current-password"
                        disabled={passwordLoading}
                        required
                      />
                    </div>

                    <div className="form-field-group">
                      <label htmlFor="newPassword">New Password</label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        autoComplete="new-password"
                        disabled={passwordLoading}
                        required
                      />
                    </div>

                    <div className="form-field-group">
                      <label htmlFor="confirmPassword">Confirm New Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        autoComplete="new-password"
                        disabled={passwordLoading}
                        required
                      />
                    </div>

                    <button type="submit" className="action-button primary" disabled={passwordLoading}>
                      {passwordLoading ? 'Updating Password...' : 'Update Password'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Server Status Card - Admin only */}
          {userIsAdmin && (
            <div className="dashboard-card">
              <h3>Server Status</h3>
              {serverHealth ? (
                <div className="health-info">
                  <div className="health-item">
                    <span className="label">Status:</span>
                    <span className={`status ${serverHealth.status}`}>
                      {serverHealth.status}
                    </span>
                  </div>
                  <div className="health-item">
                    <span className="label">Environment:</span>
                    <span>{serverHealth.environment}</span>
                  </div>
                  <div className="health-item">
                    <span className="label">Database:</span>
                    <span className={`status ${serverHealth.services?.database?.status}`}>
                      {serverHealth.services?.database?.status || 'unknown'}
                    </span>
                  </div>
                  <div className="health-item">
                    <span className="label">Uptime:</span>
                    <span>{serverHealth.uptime?.formatted || 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <p>Loading server status...</p>
              )}
            </div>
          )}

          {/* Placeholder Cards */}
          <div className="dashboard-card">
            <h3>Competitions</h3>
            <p className="placeholder-text">Participate in exciting video challenges</p>
            
            {/* Core actions should remain visible for authenticated users. */}
            <div className="card-actions">
              <button
                className="action-button primary"
                onClick={() => navigate('/competitions')}
              >
                View All Competitions
              </button>
              
              <PermissionGuard permission="CREATE_COMPETITION">
                <button
                  className="action-button"
                  onClick={() => navigate('/competitions/new')}
                >
                  + Create Competition
                </button>
              </PermissionGuard>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Your Videos</h3>

            {videoSummary.loading ? (
              <p className="placeholder-text">Loading your latest submissions...</p>
            ) : videoSummary.error ? (
              <p className="placeholder-text">{videoSummary.error}</p>
            ) : videoSummary.totalCount === 0 ? (
              <div className="video-summary-empty">
                <p className="placeholder-text">You have not submitted any videos yet.</p>
                <p className="video-summary-subtext">Join an active competition to publish your first entry.</p>
              </div>
            ) : (
              <div className="video-summary-content">
                <div className="video-summary-stats">
                  <span className="video-summary-count">{videoSummary.totalCount}</span>
                  <span className="video-summary-label">total submitted videos</span>
                </div>

                {getYouTubeEmbedUrl(videoSummary.latestSubmission?.videoUrl) ? (
                  <div className="dashboard-video-preview">
                    <iframe
                      src={getYouTubeEmbedUrl(videoSummary.latestSubmission?.videoUrl)}
                      title="Latest submitted video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (
                  <p className="video-summary-subtext">Latest video preview is unavailable for this link format.</p>
                )}

                <div className="video-summary-meta">
                  <strong>Latest submission</strong>
                  <span>{formatSubmissionDate(videoSummary.latestSubmission?.submittedAt)}</span>
                  {videoSummary.latestCompetition?.name && (
                    <span>Competition: {videoSummary.latestCompetition.name}</span>
                  )}
                  {videoSummary.latestSubmission?.status && (
                    <span>Status: {videoSummary.latestSubmission.status}</span>
                  )}
                </div>
              </div>
            )}

            <div className="card-actions">
              <button className="action-button primary" onClick={handleSubmitVideo}>
                + Submit Video
              </button>
              
              <button className="action-button" onClick={handleViewAllSubmissions}>
                View All Submissions
              </button>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Leaderboard</h3>
            <p className="placeholder-text">Coming soon...</p>
          </div>
          
          {/* Admin Panel - Only visible to admins */}
          <PermissionGuard role="admin">
            <div className="dashboard-card admin-card">
              <h3>Admin Panel</h3>
              <div className="card-actions">
                <PermissionGuard permission="MANAGE_USERS">
                  <button className="action-button">
                    Manage Users
                  </button>
                </PermissionGuard>
                
                <PermissionGuard permission="ASSIGN_ROLES">
                  <button
                    className="action-button"
                    onClick={() => navigate('/admin/roles')}
                  >
                    Assign Roles
                  </button>
                </PermissionGuard>
                
                <PermissionGuard permission="VIEW_ANALYTICS">
                  <button className="action-button">
                    View Analytics
                  </button>
                </PermissionGuard>
                
                <PermissionGuard permission="SYSTEM_CONFIG">
                  <button className="action-button">
                    System Configuration
                  </button>
                </PermissionGuard>
              </div>
            </div>
          </PermissionGuard>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;

async function fetchAllCompetitions() {
  const allCompetitions = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const response = await competitionService.getAll({ limit: 100, offset });
    const competitions = Array.isArray(response?.competitions) ? response.competitions : [];

    allCompetitions.push(...competitions);
    hasMore = Boolean(response?.hasMore);
    offset += competitions.length;

    if (competitions.length === 0) {
      hasMore = false;
    }
  }

  return allCompetitions;
}

function getLatestSubmission(submissions) {
  if (!Array.isArray(submissions) || submissions.length === 0) {
    return null;
  }

  return submissions.slice().sort((left, right) => {
    return Number(right.submittedAt || 0) - Number(left.submittedAt || 0);
  })[0];
}

function formatSubmissionDate(timestamp) {
  if (!timestamp) {
    return 'Date unavailable';
  }

  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function getYouTubeEmbedUrl(urlValue) {
  const videoId = extractYouTubeVideoId(urlValue);
  if (!videoId) {
    return '';
  }

  return `https://www.youtube.com/embed/${videoId}`;
}

function extractYouTubeVideoId(urlValue) {
  if (!urlValue || typeof urlValue !== 'string') {
    return null;
  }

  try {
    const parsedUrl = new URL(urlValue);
    const host = parsedUrl.hostname.toLowerCase();

    if (host.includes('youtu.be')) {
      return parsedUrl.pathname.replace(/^\//, '') || null;
    }

    if (host.includes('youtube.com')) {
      if (parsedUrl.pathname.startsWith('/shorts/')) {
        return parsedUrl.pathname.replace('/shorts/', '').split('/')[0] || null;
      }

      if (parsedUrl.pathname.startsWith('/embed/')) {
        return parsedUrl.pathname.replace('/embed/', '').split('/')[0] || null;
      }

      return parsedUrl.searchParams.get('v') || null;
    }
  } catch (error) {
    return null;
  }

  return null;
}
